import { ReactNode } from 'react';
import { ReactComponent as CheckIcon } from '../../../assets/icons/checkbox-circle-fill.svg';
import { ReactComponent as ErrorIcon } from '../../../assets/icons/error-warning-fill.svg';
import { ReactComponent as InfoIcon } from '../../../assets/icons/information-fill.svg';
import { useTheme } from '../../../context/NbThemeContext';
import { cssWhen } from '../../utils/themeUtils';
import classes from './FeedbackBox.module.scss';

export interface FeedbackBoxProps {
  icon?: ReactNode;
  title?: string;
  message?: string | ReactNode;
  details?: Array<string>;
  type: 'info' | 'warning' | 'error' | 'confirm';
  backgroundBold?: boolean;
  className?: string;
  isWithoutBorder?: boolean;
  dataTestid?: string;
}

export const FeedbackBox = ({
  icon,
  title,
  message,
  details,
  type,
  backgroundBold,
  className,
  isWithoutBorder,
  dataTestid,
}: FeedbackBoxProps) => {
  const { isDark } = useTheme();
  const icons = {
    confirm: <CheckIcon />,
    info: <InfoIcon />,
    warning: <ErrorIcon />,
    error: <ErrorIcon />,
  };

  const displayedIcon = icon ?? icons[type];
  const isBackgroundBold = backgroundBold ?? isDark;

  return (
    <div
      data-testid={dataTestid}
      className={cssWhen(
        className,
        classes.feedback,
        [type === 'info', classes.feedbackIsInfo],
        [type === 'warning', classes.feedbackIsWarning],
        [type === 'error', classes.feedbackIsError],
        [type === 'confirm', classes.feedbackIsConfirm],
        [type === 'warning' && isBackgroundBold, classes.feedbackIsWarningIsBold],
        [type === 'info' && isBackgroundBold, classes.feedbackIsInfoIsBold],
        [type === 'error' && isBackgroundBold, classes.feedbackIsErrorIsBold],
        [type === 'confirm' && isBackgroundBold, classes.feedbackIsConfirmIsBold],
        [type === 'warning' && isWithoutBorder, classes.feedbackIsWarningIsWithoutBorder],
        [type === 'error' && isWithoutBorder, classes.feedbackIsErrorIsWithoutBorder],
        [title, classes.feedbackIsTop],
      )}
    >
      {displayedIcon && <div className={classes.feedbackIcon}>{displayedIcon}</div>}

      <div className={classes.feedbackContent}>
        {title && <h4 className={classes.feedbackTitle}>{title}</h4>}
        {message && <p className={classes.feedbackMessage}>{message}</p>}
        {details && (
          <ul className={classes.feedbackDetails}>
            {details.map((detail, index) => (
              <li key={index}>{detail}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
